import { Autocomplete, TextField } from '@mui/material';
import { useMemo, type ComponentProps, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import {
  CHL_EVENTS,
  COMMON_EVENTS,
  MILB_EVENTS,
  MLB_EVENTS,
  MLS_EVENTS,
  NBA_EVENTS,
  NCAAF_EVENTS,
  NFL_EVENTS,
  NHL_EVENTS,
  NWSL_EVENTS,
  getEventLabel,
  type EventValue,
} from '../../lib/utils';

export interface EventAutocompleteProps
  extends Omit<ComponentProps<typeof Autocomplete<EventValue>>, 'options' | 'renderInput'> {
  error?: boolean;
  helperText?: ReactNode;
  name?: string;
  signalType: string;
}

export const EventAutocomplete = ({
  error,
  helperText,
  name,
  signalType,
  ...props
}: EventAutocompleteProps) => {
  const { t } = useTranslation();

  const options = useMemo(
    () => [
      ...COMMON_EVENTS.map((x) => x.value),
      ...(signalType === 'MILB' ? MILB_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'MLB' ? MLB_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'MLS' ? MLS_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'NWSL' ? NWSL_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'NBA' ? NBA_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'NFL' ? NFL_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'NCAAF' ? NCAAF_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'NHL' ? NHL_EVENTS.map((x) => x.value) : []),
      ...(signalType === 'CHL' ? CHL_EVENTS.map((x) => x.value) : []),
    ],
    [signalType],
  );

  return (
    <Autocomplete
      fullWidth
      getOptionLabel={(option) => getEventLabel(option, signalType)}
      options={options}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          name={name}
          {...namedByPlaceholder(
            params,
            t('pages.device_signals.settings.placeholders.select_event'),
          )}
        />
      )}
      {...props}
    />
  );
};
